import React, { useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import { STACItem } from '../types/stac';
import { RWANDA_CONFIG } from '../config/rwanda';

// Import leaflet marker images properly for Vite
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface MapViewerProps {
  selectedProvince: string | null;
  selectedItems: STACItem[];
}

// Add a component to handle map reference
const MapController: React.FC<{ selectedProvince: string | null }> = ({ selectedProvince }) => {
  const map = useMap();
  
  useEffect(() => {
    if (selectedProvince && map) {
      const province = RWANDA_CONFIG.PROVINCES.find(p => p.name === selectedProvince);
      if (province) {
        const bounds = L.latLngBounds([
          [province.bbox[1], province.bbox[0]],
          [province.bbox[3], province.bbox[2]]
        ]);
        map.fitBounds(bounds);
      }
    }
  }, [selectedProvince, map]);

  return null;
};

const MapViewer: React.FC<MapViewerProps> = ({
  selectedProvince,
  selectedItems
}) => {

  const renderItems = () => {
    return selectedItems.map(item => {
      if (!item.geometry || item.geometry.type !== 'Polygon') return null;
      
      const coordinates = item.geometry.coordinates[0];
      if (Array.isArray(coordinates) && coordinates.length > 0) {
        // Create a proper GeoJSON feature with correct coordinate typing
        const geoJsonFeature: GeoJSON.Feature<GeoJSON.Polygon> = {
          type: 'Feature',
          properties: {
            id: item.id,
            collection: item.collection
          },
          geometry: {
            type: 'Polygon',
            coordinates: item.geometry.coordinates as number[][][]
          }
        };
        
        return (
          <GeoJSON
            key={item.id}
            data={geoJsonFeature}
            style={{
              color: '#3B82F6',
              weight: 2,
              fillColor: '#3B82F6',
              fillOpacity: 0.1
            }}
          />
        );
      }
      return null;
    });
  };

  return (
    <div className="h-full relative">
      <MapContainer
        center={[-2.0, 29.9]} // Rwanda center
        zoom={8}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <MapController selectedProvince={selectedProvince} />
        {renderItems()}
      </MapContainer>
      
      {/* Map overlay info */}
      <div className="absolute top-4 right-4 bg-white bg-opacity-90 p-3 rounded-lg shadow-lg">
        <div className="text-sm">
          <div className="font-medium">
            {selectedProvince ? `${selectedProvince} Province` : 'All Rwanda'}
          </div>
          <div className="text-gray-600">
            {selectedItems.length} items displayed
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapViewer;